import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { movies, regionOptions, yearOptions, genreOptions } from '@/mocks/movies';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/feature/Pagination';
import FilterGroup from '@/components/feature/FilterGroup';

const PAGE_SIZE = 8;

const sortOptions = [
  { value: 'updated', label: '按更新时间' },
  { value: 'hot', label: '按热度' },
  { value: 'rating', label: '按评分' },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const categoryType = type === 'tv' ? 'tv' : 'movie';
  const categoryName = categoryType === 'tv' ? '电视剧' : '电影';

  const region = searchParams.get('region') ?? '';
  const year = searchParams.get('year') ?? '';
  const genre = searchParams.get('genre') ?? '';
  const sort = searchParams.get('sort') ?? 'updated';
  const page = Number(searchParams.get('page') ?? '1') || 1;

  useDocumentMeta({
    title: `${categoryName} - 神马影院影视资源分类`,
    description: `神马影院${categoryName}频道为你整理精选${categoryName}资源，可按地区、年份、类型筛选，支持按更新时间、热度与评分排序，快速找到你想看的${categoryName}。`,
    keywords: `神马影院,${categoryName},${categoryName}资源,${categoryName}排行榜,最新${categoryName},高清${categoryName}`,
    path: `/category/${categoryType}`,
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      if (movie.type !== categoryType) return false;
      if (region && movie.region !== region) return false;
      if (year && String(movie.year) !== year) return false;
      if (genre && !movie.genres.includes(genre)) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === 'hot') return b.views - a.views;
      if (sort === 'rating') return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [categoryType, region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeFilterCount = [region, year, genre].filter(Boolean).length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-700">{categoryName}</span>
      </nav>

      <div className="mb-6 flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {categoryName}频道
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
            汇集神马影院精选{categoryName}资源，可按地区、年份与类型多条件筛选，并支持按更新时间、热度、评分排序，帮助你快速定位想看的作品。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-600">
            <i className="ri-stack-line text-accent-500"></i> 共 {filtered.length} 部
          </span>
          <button
            type="button"
            onClick={() => setFilterOpen((prev) => !prev)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 md:hidden"
          >
            <i className="ri-filter-3-line"></i> 筛选{activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_240px]">
        <div>
          <div className={`${filterOpen ? 'block' : 'hidden'} mb-5 rounded-xl border border-background-200 bg-background-50 p-4 md:block`}>
            <FilterGroup label="地区" options={regionOptions} value={region} onChange={(value) => updateParam('region', value)} />
            <FilterGroup label="年份" options={yearOptions} value={year} onChange={(value) => updateParam('year', value)} />
            <FilterGroup label="类型" options={genreOptions} value={genre} onChange={(value) => updateParam('genre', value)} />
            {activeFilterCount > 0 && (
              <div className="pt-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-refresh-line"></i> 重置筛选
                </button>
              </div>
            )}
          </div>

          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                {visible.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination
                current={safePage}
                totalPages={totalPages}
                onChange={(nextPage) => updateParam('page', String(nextPage))}
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-2-line text-3xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-800">没有找到匹配的{categoryName}</h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                当前筛选条件过于严格，试着放宽地区、年份或类型，看看更多精彩内容。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
              >
                <i className="ri-refresh-line"></i> 重置筛选条件
              </button>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-sort-desc text-accent-500"></i> 排序方式
            </h3>
            <div className="flex flex-col gap-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateParam('sort', option.value)}
                  className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                    sort === option.value
                      ? 'bg-primary-50 font-semibold text-primary-700'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {option.label}
                  {sort === option.value && <i className="ri-check-line"></i>}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-links-line text-secondary-500"></i> 其他分类
            </h3>
            <div className="space-y-1">
              <Link
                to="/category/movie"
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  categoryType === 'movie' ? 'bg-primary-50 font-semibold text-primary-700' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className="ri-film-line"></i> 电影频道
              </Link>
              <Link
                to="/category/tv"
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  categoryType === 'tv' ? 'bg-primary-50 font-semibold text-primary-700' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className="ri-tv-2-line"></i> 电视剧频道
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}